import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";

const DataMappingColumnTypeSchema = z.enum([
  "string",
  "integer",
  "float",
  "numeric",
  "boolean",
  "date",
]);

const DataMappingColumnSnapshotSchema = z.object({
  name: z.string().min(1),
  type: DataMappingColumnTypeSchema,
});

/**
 * Socket id for a mapping column on `use_data_mapping` — both the per-column
 * input sockets and single-match mode's per-column outputs.
 *
 * Column names come from user data (CSV headers), and socket ids double as
 * the keys of the runtime input/output payloads, so bare column names share a
 * namespace with the step's own keys (`found`, `row`, orchestration ids like
 * `__flow_control_in__`). The prefix keeps the two namespaces disjoint by
 * construction — no reserved-name list to maintain or silently collide with.
 */
export function dataMappingColumnSocketId(column: string): string {
  return `col_${column}`;
}

/**
 * Use Data Mapping
 *
 * Matches rows in a data mapping — the org-level, stateful lookup table that
 * joins a stateful table to a schema definition (`wfv2_data_mappings`) — on a
 * column set the workflow author picks.
 *
 * The standard choices are the mapping's unique sets — its key and its unique
 * indexes — because matching on one of those is guaranteed to hit at most one
 * row. Any single column is still selectable for a deliberate list-shaped
 * match.
 *
 * The picked set drives the step's shape in both directions:
 *  - one input socket per column in the set, each typed from the design-time
 *    `columns` snapshot (so socket generation stays synchronous);
 *  - the result is every row whose values equal all of the inputs (AND).
 *
 * `requireSingleMatch` collapses the list: no match stays an ordinary
 * `found: false`, one match becomes the single `row`, and two or more fail that
 * row so the ambiguity is listed as a data problem rather than crashing the
 * chunk around it. The config renderer defaults it on when the picked set is a
 * unique index.
 *
 * Every returned row carries the mapping's key columns whatever the match set
 * was — a row you cannot address is no use for the follow-up write that is
 * usually the point of the lookup — and that is not configurable.
 */
export const useDataMappingStep = defineStepConfig({
  type: "use_data_mapping",
  name: "Use Data Mapping",
  description:
    "Matches rows in a data mapping on a chosen column set — the mapping's key and unique indexes are the standard options. Emits every matching row (or exactly one, with requireSingleMatch). The mapping's key columns are always included so a match can be addressed downstream.",
  category: "Data Access",

  // One input per configured match column, keyed by column name. The sockets'
  // types are generated per-column (see `generateUseDataMappingInputSockets`);
  // this schema only bounds what the runtime accepts, since a static schema
  // cannot know the column set.
  //
  // Nullable: the handler treats a null match value as a reported miss (the
  // row is skipped downstream), and CDC delete rows carry null for every
  // column outside the replica-identity key, so a batch that includes a
  // delete must reach the handler rather than die at input validation.
  inputSchema: z.record(
    z.string(),
    z
      .union([z.string(), z.number(), z.boolean()])
      .nullable()
      .describe(
        "Value to match against the same-named column. Null is a reported miss, never an error.",
      ),
  ),

  outputSchema: z
    .object({
      found: z.boolean().describe("At least one row matched"),
      // Exactly one of these is emitted, decided by `requireSingleMatch`; the
      // dynamic output generator publishes only the one that applies, so a graph
      // can never wire the shape the step will not produce.
      rows: z
        .array(z.record(z.string(), z.unknown()))
        .optional()
        .describe(
          "Matching rows as column name → cell value. Empty on no match.",
        ),
      row: z
        .record(z.string(), z.unknown())
        .optional()
        .describe(
          "The single matching row, when requireSingleMatch is set. Empty on no match.",
        ),
    })
    // Single-match mode also fans the row into one typed output per column.
    // catchall, because output validation `.parse()`s the handler's return
    // and a plain z.object would silently strip those column keys.
    .catchall(z.unknown()),

  configSchema: z.object({
    // `onItemError: "continue"` rather than the engine default of `fail-fast`:
    // an ambiguous or drifted mapping row is a data problem the operator needs
    // listed, and one bad row must not take down the other 4,999.
    batch: WorkflowStepBatchConfigSchema.prefault({
      size: 5000,
      onItemError: "continue",
      concurrency: 8,
    }),
    dataMappingSlug: z
      .string()
      .min(1)
      .describe('Slug of the data mapping to read (e.g. "warehouse-master")'),
    matchColumns: z
      .array(z.string().min(1))
      .min(1)
      .max(8)
      .describe(
        "Column set to match on — every column must equal its input (AND). The mapping's key and unique indexes are the standard choices.",
      ),
    matchColumnsUnique: z
      .boolean()
      .optional()
      .describe(
        "Design-time note that the picked set was a unique index (key, enforced, or observed) when configured. Surfaced as drift in the editor when the mapping stops backing it; no runtime effect.",
      ),
    requireSingleMatch: z
      .boolean()
      .default(false)
      .describe(
        "Collapse the output to a single row. No match is still found:false; two or more fail the row.",
      ),
    columns: z
      .array(DataMappingColumnSnapshotSchema)
      .min(1)
      .describe(
        "Snapshot of the mapping's column schema at design time. Types the matchValue input socket; re-checked at runtime and refused if the mapping has since changed.",
      ),
    keyColumns: z
      .array(z.string().min(1))
      .min(1)
      .describe(
        "Snapshot of the mapping's key columns, in key order, at design time. Re-checked at runtime.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: { maximumAttempts: 2 },
  },
});
